/**
 * Shared, versioned postMessage protocol used between PU (host) and dtrader-c (iframe).
 *
 * MUST stay in lockstep with PU/src/utils/dtrader-iframe-protocol.ts. Bumping `PROTOCOL` requires a
 * coordinated release of both apps; the iframe should treat unknown versions as a hard auth error.
 */

export const PROTOCOL = 'everyman-dtrader/1' as const;
export type TProtocolVersion = typeof PROTOCOL;

export type TLegacyAccountDetails = {
    loginid: string;
    currency: string;
    is_virtual?: 0 | 1;
    account_type?: string;
    landing_company_name?: string;
    broker?: string;
};

export type TOAuth2Account = {
    account_id: string;
    currency: string;
    is_virtual?: 0 | 1;
    account_type?: 'demo' | 'real';
};

export type TLegacyAuthPayload = {
    flow: 'legacy';
    active_loginid: string;
    tokens: Record<string, string>;
    account_details: Record<string, TLegacyAccountDetails>;
};

export type TOAuth2AuthPayload = {
    flow: 'oauth2';
    active_account_id: string;
    access_token: string;
    expires_at: number;
    account_list: TOAuth2Account[];
};

export type TAuthPayload = TLegacyAuthPayload | TOAuth2AuthPayload;

export type THostToIframeMessage =
    | { type: 'AUTH'; protocol: TProtocolVersion; payload: TAuthPayload }
    | { type: 'SWITCH'; protocol: TProtocolVersion; account: string }
    | { type: 'LOGOUT'; protocol: TProtocolVersion };

export type TIframeToHostMessage =
    | { type: 'READY'; protocol: TProtocolVersion }
    | { type: 'AUTHED'; protocol: TProtocolVersion; loginid: string }
    | { type: 'AUTH_ERROR'; protocol: TProtocolVersion; code: string; message: string };

export const isProtocolMessage = (
    data: unknown
): data is THostToIframeMessage | TIframeToHostMessage => {
    if (!data || typeof data !== 'object') return false;
    const obj = data as { protocol?: unknown; type?: unknown };
    return obj.protocol === PROTOCOL && typeof obj.type === 'string';
};
